import { useContext, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import {
  cleanObject,
  deepCopyFunction,
  formatDateYYYYMMDD,
  getDefaultStoreProperties,
  getRandomIntInclusive,
} from "../../../helpers";
import { RequestLoader, ToastContext } from "../../../hooks/context";
import { restockProduct } from "../../../services/inventory";
import {
  useGetPricingTypes,
  useGetSuppliersForSelectField,
} from "../../../hooks/api/queries/useInventory";
import { v4 as uuidv4 } from "uuid";
import { useGetAccountingBanksListConditionally } from "../../../hooks/api/queries";
import { useGetInfo } from "../../../hooks/useGetInfo";

const storeVariantProperty = {
  store_id: null,
  store_name: "",
  variants: [],
  item_level_tracking: false,
  apply_pricing_to_all_stores: false,
  uuid: uuidv4(),
};

const getVariantsList = (variants) => {
  let newVariants = [];
  variants?.forEach((item) => {
    newVariants.push({ name: `${item?.option} - ${item.value}`, id: item?.id });
  });
  return newVariants;
};

const useAddProductStock = ({ selectedProduct, closeWidget }) => {
  const { storeList, businessDetails } = useSelector(
    (state) => state.profileDetailsReducer,
  );
  const [storeProperties, setStoreProperties] = useState([
    {
      ...getDefaultStoreProperties(),
      uuid: uuidv4(),
    },
  ]);
  const [selectedStore, setSelectedStore] = useState([]);

  const { isLoading: isLoadingBankAccountsList, data: bankAccount } =
    useGetAccountingBanksListConditionally({
      isShowBankAccounts: true,
      // isShowCashOnHandAccount: true,s
    });

  const [storeVariantProperties, setStoreVariantProperties] = useState([
    {
      ...storeVariantProperty,
    },
  ]);
  const [variantsList] = useState(getVariantsList(selectedProduct?.variants));
  const triggerToast = useContext(ToastContext);

  const { setRequestLoaderProgress } = useContext(RequestLoader);
  const queryClient = useQueryClient();
  const { data: pricingTypes, isLoading: isLoadingPricingTypes } =
    useGetPricingTypes();
  const { loadOptions } = useGetSuppliersForSelectField({});

  const [modalViews, setModalViews] = useState({
    isWarning: false,
  });
  const { getStoreInfo, getPricingInfo } = useGetInfo();
  const [detectedPricing, setDetectedPricing] = useState([]);

  const resetModalViews = (key, value = true) => {
    setModalViews({
      isWarning: false,
      [key]: value,
    });
  };

  const setPropertiesBasedOnStore = (storeId, storePropertyIndex) => {
    if (storeProperties?.find((item) => item.store_id === storeId))
      return triggerToast("This store has been selected", "warning");

    const newStoreProperties = deepCopyFunction(storeProperties);
    const selectedProductStoreProperties =
      selectedProduct?.store_product_properties?.find(
        (item) => item?.store_id === storeId,
      );
    newStoreProperties[storePropertyIndex].store_id = storeId;
    newStoreProperties[storePropertyIndex].store_name = storeList?.find(
      (item) => item.id === storeId,
    )?.name;
    newStoreProperties[storePropertyIndex].cost_price =
      selectedProductStoreProperties?.cost_price;
    newStoreProperties[storePropertyIndex].pricings[0] = {
      ...newStoreProperties[storePropertyIndex].pricings[0],
      pricing_type_id:
        selectedProductStoreProperties?.pricings?.[0]?.pricing_type?.id,
      price: selectedProductStoreProperties?.pricings?.[0]?.price,
      apply_pricing_to_all_stores:
        selectedProductStoreProperties?.apply_pricing_to_all_stores,
      discount_rate:
        selectedProductStoreProperties?.pricings?.[0]?.discount_rate || "",
    };
    newStoreProperties[storePropertyIndex].expiry_date =
      selectedProductStoreProperties?.expiry_date || "";
    newStoreProperties[storePropertyIndex].vat_rate =
      selectedProduct?.vat ||
      selectedProduct?.stores?.find((store) => store?.id === storeId)?.vat;

    setStorePropertiesFunc(newStoreProperties);
  };

  const addMoreStoreOption = () => {
    const newStoreProperties = [...storeProperties];
    newStoreProperties.push({
      ...getDefaultStoreProperties(),
    });
    setStorePropertiesFunc(newStoreProperties);
  };

  const deleteStoreProperty = (storePropertyIndex) => {
    const newStoreProperties = [...storeProperties];
    newStoreProperties.splice(storePropertyIndex, 1);
    setStorePropertiesFunc(newStoreProperties);
  };

  const setStorePropertiesFunc = (newStoreProperties) => {
    setStoreProperties(newStoreProperties);
    window.setAddStockFormValue("storeProperties", newStoreProperties);
  };

  const setVariantPropertyBasedOnStore = (storeId, storeVariantIndex) => {
    if (storeVariantProperties?.find((item) => item.store_id === storeId))
      return triggerToast("This store has been selected", "warning");

    const newStoreVariantProperties = deepCopyFunction(storeVariantProperties);
    newStoreVariantProperties[storeVariantIndex].store_id = storeId;
    newStoreVariantProperties[storeVariantIndex].store_name = storeList?.find(
      (item) => item?.id === storeId,
    )?.name;
    setStoreVariantPropertiesFunc(newStoreVariantProperties);
  };

  const setPropertiesBasedOnVariantId = (variantsIds, storeVariantIndex) => {
    const newStoreVariantProperties = deepCopyFunction(storeVariantProperties);
    newStoreVariantProperties[storeVariantIndex].variants = [];
    variantsIds?.forEach((variantId) => {
      const variantsProperties = selectedProduct?.variants?.find(
        (item) => item?.id === variantId,
      );
      const storeVariantProperty = variantsProperties?.store_variant_properties;
      const variantProperty = storeVariantProperty?.find(
        (item) =>
          item?.store_id ===
          newStoreVariantProperties?.[storeVariantIndex]?.store_id,
      );

      newStoreVariantProperties[storeVariantIndex].variants.push({
        variant_id: variantId,
        variant_name: variantsList?.find((item) => item?.id === variantId)
          ?.name,
        stock_quantity: "",
        batch_name: "",
        restock_date: formatDateYYYYMMDD(Date.now()),
        expiry_date: variantProperty?.expiry_date || "",
        barcode: variantsProperties?.barcode || "",
        cost_price: variantProperty?.cost_price || "",
        items_number: [],
        pricings: [
          {
            price: variantProperty?.pricings?.[0].price,
            pricing_type_id: variantProperty?.pricings?.[0].pricing_type?.id,
            discount_rate: variantProperty?.pricings?.[0].discount_rate,
            uuid: uuidv4(),
          },
        ],
        apply_pricing_to_all_stores:
          variantProperty?.apply_pricing_to_all_stores,
        vat_rate:
          selectedProduct?.vat ||
          selectedProduct?.stores?.find(
            (store) =>
              store?.id ===
              newStoreVariantProperties[storeVariantIndex]?.store_id,
          )?.vat,
      });
    });

    setStoreVariantPropertiesFunc(newStoreVariantProperties);
  };

  const addStoreVariantOption = () => {
    const newStoreVariantProperties = [...storeVariantProperties];
    newStoreVariantProperties.push({ ...storeVariantProperty });
    setStoreVariantPropertiesFunc(newStoreVariantProperties);
  };

  const deleteStoreVariantOption = (storeVariantIndex) => {
    const newStoreVariantProperties = [...storeVariantProperties];
    newStoreVariantProperties.splice(storeVariantIndex, 1);
    setStoreVariantPropertiesFunc(newStoreVariantProperties);
  };

  const setStoreVariantPropertiesFunc = (newStoreVariantProperties) => {
    setStoreVariantProperties(newStoreVariantProperties);
    window.setAddStockFormValue(
      "storeVariantProperties",
      newStoreVariantProperties,
    );
  };

  const updateSupplierAcrossStore = (supplierId, itemIndex, status) => {
    const newStoreProperties = deepCopyFunction(storeProperties);
    newStoreProperties?.forEach((item, index) => {
      if (index !== itemIndex) {
        item.useSupplierAcross = false;
        item.supplier = status ? supplierId : null;
      }
    });

    setStoreProperties(newStoreProperties);
  };

  const updateAccountAcrossStore = (accountId, itemIndex, status) => {
    const newStoreProperties = deepCopyFunction(storeProperties);
    newStoreProperties?.forEach((item, index) => {
      if (index !== itemIndex) {
        item.useAccountAcross = false;
        item.ledger_account_id = status ? accountId : null;
      }
    });

    setStoreProperties(newStoreProperties);
  };

  const updateSupplierAcrossStoreVariant = (
    storeVariantIndex,
    variantIndex,
    supplierId,
    status,
  ) => {
    const newStoreVariantProperties = deepCopyFunction(storeVariantProperties);
    newStoreVariantProperties?.forEach((storeItem, storeIndex) => {
      storeItem?.variants?.forEach((item, itemIndex) => {
        if (storeIndex !== storeVariantIndex || itemIndex !== variantIndex) {
          item.useSupplierAcross = false;
          item.supplier = status ? supplierId : null;
        }
      });
    });
    setStoreVariantPropertiesFunc(newStoreVariantProperties);
  };

  const { mutate: addProductStock, isLoading: isAddingStock } = useMutation(
    (data) => restockProduct(data),
  );
  const addStock = () => {
    let newDetectedPricing = [];
    setRequestLoaderProgress(getRandomIntInclusive(10, 30));
    const submissionData = {};

    if (selectedProduct?.variants?.length === 0) {
      submissionData.has_variants = false;
      submissionData.restock_data = [...storeProperties]?.map((item) => {
        const pricings = item?.pricings?.map((pricingItem) => {
          if (Number(item?.cost_price) > Number(pricingItem?.price))
            newDetectedPricing.push({
              store_name: item?.store_name,
              store_currency: getStoreInfo(item?.store_id)?.currency_code,
              cost_price: item?.cost_price,
              selling_price: pricingItem?.price,
              pricing_name: getPricingInfo(pricingItem?.pricing_type_id)?.label,
            });
          return cleanObject(pricingItem, ["uuid"]);
        });

        return cleanObject({ ...item, pricings }, [
          "stock_quantity",
          "minimum_stock_quantity",
          "uuid",
          `${item?.item_level_tracking ? "" : "items_number"}`,
        ]);
      });
    } else {
      submissionData.has_variants = true;
      const newRestock = [];
      storeVariantProperties?.forEach((storeVariantProperty) => {
        storeVariantProperty?.variants?.forEach((variantItem) => {
          newRestock.push(
            cleanObject({
              store_id: storeVariantProperty?.store_id,
              expiry_date: variantItem?.expiry_date,
              restock_date: variantItem?.restock_date,
              barcode: variantItem?.barcode,
              quantity: variantItem?.stock_quantity,
              batch_name: variantItem?.batch_name,
              variant_id: variantItem?.variant_id,
              cost_price: variantItem?.cost_price,
              supplier: variantItem?.supplier,
              vat_rate: variantItem?.vat_rate,
              ledger_account_id: storeVariantProperty?.ledger_account_id,
              apply_pricing_to_all_stores:
                variantItem?.apply_pricing_to_all_stores,
              items_number: storeVariantProperty?.item_level_tracking
                ? variantItem?.items_number
                : undefined,
              pricings: variantItem?.pricings?.map((pricingItem) => {
                if (
                  Number(variantItem?.cost_price) > Number(pricingItem?.price)
                )
                  newDetectedPricing.push({
                    store_name: storeVariantProperty?.store_name,
                    store_currency: getStoreInfo(storeVariantProperty?.store_id)
                      ?.currency_code,
                    cost_price: variantItem?.cost_price,
                    selling_price: pricingItem?.price,
                    pricing_name: getPricingInfo(
                      pricingItem?.pricing_type_id || 1,
                    )?.label,
                  });

                return cleanObject(pricingItem, ["uuid"]);
              }),
            }),
          );
        });
      });
      submissionData.restock_data = newRestock;
    }

    // return a warning modal
    if (newDetectedPricing?.length > 0 && !modalViews?.isWarning) {
      setDetectedPricing(newDetectedPricing);
      setRequestLoaderProgress(100);
      return resetModalViews("isWarning", true);
    }

    resetModalViews("isWarning", false);
    addProductStock(
      { id: selectedProduct?.id, payload: submissionData },
      {
        onSuccess: () => {
          triggerToast("Product Updated Sucessfully", "success");
          queryClient.invalidateQueries("getProductList");
          queryClient.invalidateQueries("getCompositeProductList");
          queryClient.invalidateQueries(
            "getProductDetails",
            `${selectedProduct?.id}`,
          );
          queryClient.invalidateQueries("getTrackItems");
          closeWidget();
        },
        onSettled: () => setRequestLoaderProgress(100),
      },
    );
  };

  return {
    storeList,
    storeProperties,
    isAddingStock,
    storeVariantProperties,
    variantsList,
    pricingTypes,
    isLoadingPricingTypes,
    loadOptions,
    setStoreProperties,
    addMoreStoreOption,
    setPropertiesBasedOnStore,
    deleteStoreProperty,
    addStock,
    detectedPricing,
    modalViews,
    resetModalViews,
    isLoadingBankAccountsList,
    bankAccount,
    updateAccountAcrossStore,
    setSelectedStore,
    selectedStore,
    setStoreVariantProperties,
    setVariantPropertyBasedOnStore,
    setPropertiesBasedOnVariantId,
    addStoreVariantOption,
    deleteStoreVariantOption,
    updateSupplierAcrossStore,
    updateSupplierAcrossStoreVariant,
    businessDetails,
  };
};
export default useAddProductStock;
